import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formatter } from "../../../../services/formatter/formatter.service";
import { Biometric, BiometricType } from "../../../../models/entities/biometrics.model";
import UiFieldMessage from "../../../../components/field-message/FieldMessage.component";
import { FieldMessageSeverity } from "../../../../models/components/field-message.model";
import { Chart } from "primereact/chart";
import { BiometricsFormatter } from "../../../../services/biometrics/biometrics-formatter.service";
import {
	CHART_DATA,
	CHART_DATASET,
	CHART_FILTER,
	CHART_TYPE,
} from "../../../../models/pages/biometrics/biometrics-charts.model";
import BiometricsChartsChartSummary from "./BiometricsChartsChartSummary";
import "chartjs-adapter-moment";
import { BiometricsHelper } from "../../../../services/biometrics/biometrics-helper.service";
import { useTrends } from "../../../../hooks/useTrends";
import { format, startOfDay, subDays, subMonths } from "date-fns";
import { ParticipantContextProps } from "../../../../models/contexts/participant-context.model";
import { ParticipantContext } from "../../../../contexts/Participant.context";
import { Trends } from "../../../../models/entities/trends.model";
import { useHealthyRanges } from "../../../../hooks/useHealthyRanges";
import { HealthyRange } from "../../../../models/entities/healthy-ranges.model";

const chartColors = {
	blue: "rgba(54, 162, 235, 0.6)",
	blueBorder: "rgba(54, 162, 235, 1)",
	blueLine: "rgba(54, 162, 235, 0.8)",
	green: "rgba(75, 192, 192, 0.6)",
	greenBorder: "rgba(75, 192, 192, 1)",
	greenLine: "rgba(75, 192, 192, 0.8)",
	red: "rgba(255, 99, 132, 0.6)",
	redBorder: "rgba(255, 99, 132, 1)",
	redLine: "rgba(255, 99, 132, 0.8)",
	purple: "rgba(153, 102, 255, 0.6)",
	purpleBorder: "rgba(153, 102, 255, 1)",
	purpleLine: "rgba(153, 102, 255, 0.8)",
	orange: "rgba(255, 159, 64, 0.6)",
	orangeBorder: "rgba(255, 159, 64, 1)",
	orangeLine: "rgba(255, 159, 64, 0.8)",
};

const BiometricsChartsChart = ({
	biometrics,
	filters,
	firstBiometric,
	onDatasetsChange,
}: {
	biometrics: Map<string, Biometric[]>;
	filters: { type: CHART_TYPE; range: CHART_FILTER };
	firstBiometric: string;
	onDatasetsChange: (e: CHART_DATA) => void;
}) => {
	const { t } = useTranslation("common");
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);

	const startDate = useMemo(() => {
		const today = startOfDay(new Date());
		const firstBiometricDate = firstBiometric ? new Date(firstBiometric) : today;

		switch (filters.range) {
			case CHART_FILTER.WEEK:
				return subDays(today, 7);
			case CHART_FILTER.MONTH:
				return subMonths(today, 1);
			case CHART_FILTER.TRIMESTER:
				return subMonths(today, 3);
			case CHART_FILTER.ALL:
				return firstBiometricDate;
			default:
				return today;
		}
	}, [filters.range, firstBiometric]);

	const endDate = useMemo(() => {
		const today = new Date();
		today.setHours(23, 59, 59, 999);
		return today;
	}, []);

	const { data: trendsData } = useTrends({
		participantId: participantContext.participant!.id,
		startDate: format(startDate, "yyyy-MM-dd"),
		biometricType: filters.type,
	});

	const { data: healthyRanges } = useHealthyRanges();

	// Generic function to create trendlines
	const addTrendlines = (
		datasets: CHART_DATASET[],
		trendsLines: Trends | undefined,
		startDate: Date,
		endDate: Date,
		type: CHART_TYPE,
		subTypesWithData: string[]
	) => {
		let trendConfigs: { key: string; color: string; label: string }[] = [];

		if (type === CHART_TYPE.GLUCOSE) {
			const allConfigs = [
				{ key: "FASTING", color: chartColors["orangeLine"], label: "Fasting Trendline" },
				{
					key: "UNLABELED",
					color: chartColors["redLine"],
					label: "Unlabeled Trendline",
				},
				{ key: "PRE-MEAL", color: chartColors["greenLine"], label: "Pre-Meal Trendline" },
				{
					key: "POST-MEAL",
					color: chartColors["purpleLine"],
					label: "Post-Meal Trendline",
				},
			];
			trendConfigs = allConfigs.filter((config) => subTypesWithData.includes(config.key));
		} else if (type === CHART_TYPE.BLOOD_PRESSURE) {
			const allConfigs = [
				{
					key: "BLOOD_PRESSURE_SYSTOLIC",
					color: chartColors["redLine"],
					label: "Systolic Trendline",
				},
				{
					key: "BLOOD_PRESSURE_DIASTOLIC",
					color: chartColors["blueLine"],
					label: "Diastolic Trendline",
				},
			];
			trendConfigs = allConfigs.filter((config) => subTypesWithData.includes(config.key));
		} else if (type === CHART_TYPE.WEIGHT) {
			trendConfigs = [
				{
					key: "WEIGHT",
					color: chartColors["blueLine"],
					label: "Weight Trendline",
				},
			];
		}

		trendConfigs.forEach(({ key, color, label }) => {
			const trend = trendsLines?.[key];
			if (trend?.startValue && trend?.endValue) {
				datasets.push({
					label,
					data: [
						{ x: startDate, y: trend.startValue },
						{ x: endDate, y: trend.endValue },
					],
					type: "line",
					borderColor: color,
					backgroundColor: color.replace("0.8", "0.1"),
					borderWidth: 2,
					pointRadius: 0,
					borderDash: [5, 5],
					fill: false,
					order: 2,
				});
			}
		});
	};

	// Generic function to create the healthy zones
	const addHealthyZones = (
		datasets: CHART_DATASET[],
		healthyRanges: HealthyRange[] | undefined,
		startDate: Date,
		endDate: Date,
		type: CHART_TYPE,
		subTypesWithData: string[]
	) => {
		if (!healthyRanges || healthyRanges.length === 0) return;

		let relevantRanges: {
			subType: string;
			minValue: number;
			maxValue: number;
			label: string;
		}[] = [];

		if (type === CHART_TYPE.GLUCOSE) {
			relevantRanges = healthyRanges
				.filter((range) => subTypesWithData.includes(range.subType))
				.map((range) => ({
					subType: range.subType,
					minValue: range.minValue,
					maxValue: range.maxValue,
					label: `${range.subType.charAt(0) + range.subType.slice(1).toLowerCase().replace("-", "-")} Healthy Zone`,
				}));
		} else if (type === CHART_TYPE.BLOOD_PRESSURE) {
			relevantRanges = healthyRanges
				.filter((range) => subTypesWithData.includes(range.subType))
				.map((range) => ({
					subType: range.subType,
					minValue: range.minValue,
					maxValue: range.maxValue,
					label:
						range.subType === "BLOOD_PRESSURE_SYSTOLIC"
							? "Systolic Healthy Zone"
							: "Diastolic Healthy Zone",
				}));
		}

		relevantRanges.forEach((range) => {
			// Top boundary (max value)
			datasets.push({
				label: range.label,
				data: [
					{ x: startDate, y: range.maxValue },
					{ x: endDate, y: range.maxValue },
				],
				type: "line",
				backgroundColor: "rgba(34, 197, 94, 0.15)",
				borderColor: "transparent",
				borderWidth: 0,
				pointRadius: 0,
				fill: {
					target: "+1",
					above: "rgba(34, 197, 94, 0.15)",
				},
				tension: 0,
				order: 3,
			});

			// Bottom boundary (min value) - invisible line for fill reference
			datasets.push({
				label: "",
				data: [
					{ x: startDate, y: range.minValue },
					{ x: endDate, y: range.minValue },
				],
				type: "line",
				backgroundColor: "transparent",
				borderColor: "transparent",
				borderWidth: 0,
				pointRadius: 0,
				fill: false,
				tension: 0,
				order: 3,
			});
		});
	};

	const [chartDatasets, setChartDatasets] = useState<CHART_DATA>();

	useEffect(() => {
		mapDatasets();
	}, [filters, trendsData]);

	/* Chart configuration */

	const chartConfig = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			tooltip: {
				callbacks: {
					title: function (context: any) {
						return [];
					},
					label: function (context: any) {
						let label: string = ``;
						const date = Formatter.date(context.raw.x, {
							day: "2-digit",
							month: "2-digit",
							year: "2-digit",
							hour: "2-digit",
							minute: "2-digit",
						});

						switch (filters.type) {
							case CHART_TYPE.BLOOD_PRESSURE: {
								const values = biometrics
									.get(BiometricType.blood_pressure)
									?.find(
										(x) =>
											new Date(x.vitalTime).getTime() ===
											(context.raw.x as Date).getTime()
									);
								const formattedValue = BiometricsFormatter.formatBloodPressure(
									values?.entries.find(
										(x) => x.subType === BiometricType.blood_pressure_systolic
									)?.value,
									values?.entries.find(
										(x) => x.subType === BiometricType.blood_pressure_diastolic
									)?.value
								);
								label = `${formattedValue}; ${date}`;
								break;
							}
							case CHART_TYPE.GLUCOSE:
								label = `${BiometricsFormatter.formatGlucose(context.raw.y)}; ${date}`;
								break;
							case CHART_TYPE.WEIGHT:
								label = `${BiometricsFormatter.formatWeight(context.raw.y)}; ${date}`;
								break;
							default:
								break;
						}
						return label;
					},
				},
			},
			legend: {
				position: "right",
				align: "start",
				labels: {
					filter: function (legendItem: any) {
						// Hide legend items with empty labels
						return legendItem.text !== "";
					},
				},
			},
		},
		scales: {
			x: {
				type: "time",
				time: {
					unit: "day",
					displayFormats: { day: "MM/DD/YY" },
				},
				ticks: {
					source: "labels",
					color: "#555555",
				},
				grid: {
					color: "#F2F2F0",
				},
				// offset: true
			},
			y: {
				offset: true,
				min: 0, // Start y-axis at 0
				ticks: {
					color: "#555555",
				},
				grid: {
					color: "#F2F2F0",
				},
			},
		},
	};

	/* Chart data */

	const isWithinRange = (vitalTime: Date): boolean => {
		let value = false;
		const today: Date = new Date();

		switch (filters.range) {
			case CHART_FILTER.ALL:
				value = true;
				break;
			case CHART_FILTER.WEEK:
				if (Formatter.datesDaysDiffLocal(vitalTime, today) <= 7) {
					value = true;
				}
				break;
			case CHART_FILTER.MONTH:
				if (Formatter.datesDaysDiffLocal(vitalTime, today) <= 30) {
					value = true;
				}
				break;
			case CHART_FILTER.TRIMESTER:
				if (Formatter.datesDaysDiffLocal(vitalTime, today) <= 90) {
					value = true;
				}
				break;
			default:
				break;
		}

		return value;
	};

	const mapData = (type: BiometricType, subType?: BiometricType): { x: Date; y: number }[] => {
		const value: { x: Date; y: number }[] = [];

		const tmpData = biometrics.get(type);
		if (tmpData && tmpData.length > 0) {
			tmpData
				.filter((item) => item.mealState !== "CONTROL")
				.forEach((item) => {
					if (item.deleted) return;
					if (subType) {
						item.entries.forEach((element) => {
							if (
								element.subType === subType &&
								isWithinRange(new Date(item.vitalTime))
							) {
								value.push({ x: new Date(item.vitalTime), y: element.value });
							}
						});
					} else if (isWithinRange(new Date(item.vitalTime))) {
						value.push({ x: new Date(item.vitalTime), y: item.entries[0]?.value });
					}
				});
		}

		return value;
	};

	const mapDatasets = (): void => {
		const data: CHART_DATA = {
			labels: [],
			datasets: [],
		};

		switch (filters.type) {
			case CHART_TYPE.GLUCOSE: {
				const glucoseDataMap: Record<string, { x: Date; y: number }[]> = {
					FASTING: mapData(BiometricType.glucose, BiometricType.fasting),
					UNLABELED: mapData(BiometricType.glucose, BiometricType.unlabeled),
					"PRE-MEAL": mapData(BiometricType.glucose, BiometricType.pre_meal),
					"POST-MEAL": mapData(BiometricType.glucose, BiometricType.post_meal),
				};

				const glucoseSubTypesWithData = Object.entries(glucoseDataMap)
					.filter(([_, values]) => values.length > 0)
					.map(([key]) => key);

				const hasGlucoseData = glucoseSubTypesWithData.length > 0;

				if (hasGlucoseData) {
					data.datasets = [];

					addHealthyZones(
						data.datasets,
						healthyRanges,
						startDate,
						endDate,
						CHART_TYPE.GLUCOSE,
						glucoseSubTypesWithData
					);

					const glucoseDatasetConfig: Record<
						string,
						{
							labelKey: string;
							backgroundColor: string;
							borderColor: string;
						}
					> = {
						FASTING: {
							labelKey: "PARTICIPANT.BIOMETRICS.CHARTS.TITLE_LEGEND_GLUCOSE_FASTING",
							backgroundColor: chartColors["orange"],
							borderColor: chartColors["orangeBorder"],
						},
						UNLABELED: {
							labelKey:
								"PARTICIPANT.BIOMETRICS.CHARTS.TITLE_LEGEND_GLUCOSE_UNLABELED",
							backgroundColor: chartColors["red"],
							borderColor: chartColors["redBorder"],
						},
						"PRE-MEAL": {
							labelKey: "PARTICIPANT.BIOMETRICS.CHARTS.TITLE_LEGEND_GLUCOSE_PRE_MEAL",
							backgroundColor: chartColors["green"],
							borderColor: chartColors["greenBorder"],
						},
						"POST-MEAL": {
							labelKey:
								"PARTICIPANT.BIOMETRICS.CHARTS.TITLE_LEGEND_GLUCOSE_POST_MEAL",
							backgroundColor: chartColors["purple"],
							borderColor: chartColors["purpleBorder"],
						},
					};

					// Add datasets only for subtypes with data
					glucoseSubTypesWithData.forEach((subType) => {
						const config = glucoseDatasetConfig[subType];
						if (config) {
							data.datasets.push({
								label: t(config.labelKey),
								data: glucoseDataMap[subType],
								backgroundColor: config.backgroundColor,
								borderColor: config.borderColor,
								order: 1,
							});
						}
					});

					addTrendlines(
						data.datasets,
						trendsData,
						startDate,
						endDate,
						CHART_TYPE.GLUCOSE,
						glucoseSubTypesWithData
					);
					data.labels = [startDate, endDate];
				}
				break;
			}

			case CHART_TYPE.BLOOD_PRESSURE: {
				const bpDataMap: Record<string, { x: Date; y: number }[]> = {
					BLOOD_PRESSURE_SYSTOLIC: mapData(
						BiometricType.blood_pressure,
						BiometricType.blood_pressure_systolic
					),
					BLOOD_PRESSURE_DIASTOLIC: mapData(
						BiometricType.blood_pressure,
						BiometricType.blood_pressure_diastolic
					),
				};

				const bpSubTypesWithData = Object.entries(bpDataMap)
					.filter(([_, values]) => values.length > 0)
					.map(([key]) => key);

				const hasBPData = bpSubTypesWithData.length > 0;

				if (hasBPData) {
					data.datasets = [];

					addHealthyZones(
						data.datasets,
						healthyRanges,
						startDate,
						endDate,
						CHART_TYPE.BLOOD_PRESSURE,
						bpSubTypesWithData
					);

					const bpDatasetConfig: Record<
						string,
						{
							labelKey: string;
							backgroundColor: string;
							borderColor: string;
						}
					> = {
						BLOOD_PRESSURE_SYSTOLIC: {
							labelKey:
								"PARTICIPANT.BIOMETRICS.CHARTS.TITLE_LEGEND_BLOOD_PRESSURE_SYSTOLIC",
							backgroundColor: chartColors["red"],
							borderColor: chartColors["redBorder"],
						},
						BLOOD_PRESSURE_DIASTOLIC: {
							labelKey:
								"PARTICIPANT.BIOMETRICS.CHARTS.TITLE_LEGEND_BLOOD_PRESSURE_DIASTOLIC",
							backgroundColor: chartColors["blue"],
							borderColor: chartColors["blueBorder"],
						},
					};

					// Add datasets only for subtypes with data
					bpSubTypesWithData.forEach((subType) => {
						const config = bpDatasetConfig[subType];
						if (config) {
							data.datasets.push({
								label: t(config.labelKey),
								data: bpDataMap[subType],
								backgroundColor: config.backgroundColor,
								borderColor: config.borderColor,
								order: 1,
							});
						}
					});

					addTrendlines(
						data.datasets,
						trendsData,
						startDate,
						endDate,
						CHART_TYPE.BLOOD_PRESSURE,
						bpSubTypesWithData
					);
					data.labels = [startDate, endDate];
				}
				break;
			}

			case CHART_TYPE.WEIGHT: {
				const weightData = mapData(BiometricType.weight);
				const hasWeightData = weightData.length > 0;

				if (hasWeightData) {
					data.datasets = [
						{
							label: t("PARTICIPANT.BIOMETRICS.CHARTS.TITLE_LEGEND_WEIGHT"),
							data: weightData,
							backgroundColor: chartColors["blue"],
							borderColor: chartColors["blueBorder"],
							order: 1,
						},
					];

					addTrendlines(
						data.datasets,
						trendsData,
						startDate,
						endDate,
						CHART_TYPE.WEIGHT,
						["WEIGHT"]
					);

					data.labels = [startDate, endDate];
				}
				break;
			}
			default:
				break;
		}

		// Update state
		setChartDatasets(data);
		onDatasetsChange(data);
	};

	// Add this function to filter biometrics by date range
	const getFilteredBiometrics = (): Map<string, Biometric[]> => {
		const filteredBiometrics = new Map<string, Biometric[]>();

		biometrics.forEach((biometricArray, key) => {
			const filtered = biometricArray.filter((biometric) =>
				isWithinRange(new Date(biometric.vitalTime))
			);
			filteredBiometrics.set(key, filtered);
		});

		return filteredBiometrics;
	};

	return (
		<div className="biometrics-charts">
			{BiometricsHelper.emptyDataset(chartDatasets) ? (
				<>
					<div className="text-bold fs-5 mb-2">
						{t(
							filters.type === CHART_TYPE.GLUCOSE
								? "PARTICIPANT.BIOMETRICS.CHARTS.TITLE_CHART_GLUCOSE"
								: filters.type === CHART_TYPE.WEIGHT
									? "PARTICIPANT.BIOMETRICS.CHARTS.TITLE_CHART_WEIGHT"
									: "PARTICIPANT.BIOMETRICS.CHARTS.TITLE_CHART_BLOOD_PRESSURE"
						)}
					</div>
					<div className="k-empty-message mb-3">
						<UiFieldMessage
							severity={FieldMessageSeverity.INFO}
							label="PARTICIPANT.BIOMETRICS.CHARTS.EMPTY_MESSAGE"
						/>
					</div>
				</>
			) : (
				<>
					{/* Chart info */}
					<BiometricsChartsChartSummary
						type={filters.type}
						chartData={chartDatasets as CHART_DATA}
						biometrics={getFilteredBiometrics()}
					/>

					{/* Charts: Glucose / Blood pressure / Weight */}
					<div className="text-bold fs-5 mt-3">
						{t(
							filters.type === CHART_TYPE.GLUCOSE
								? "PARTICIPANT.BIOMETRICS.CHARTS.TITLE_CHART_GLUCOSE"
								: filters.type === CHART_TYPE.WEIGHT
									? "PARTICIPANT.BIOMETRICS.CHARTS.TITLE_CHART_WEIGHT"
									: "PARTICIPANT.BIOMETRICS.CHARTS.TITLE_CHART_BLOOD_PRESSURE"
						)}
					</div>
					<div className="charts">
						{chartDatasets && chartConfig && (
							<Chart
								type="scatter"
								data={chartDatasets}
								options={chartConfig}
								height="300px"
							/>
						)}
					</div>
				</>
			)}
		</div>
	);
};
export default BiometricsChartsChart;
